// src/context/AuthContext.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../services/api";

type User = { id?: string | number; name?: string; email?: string; role?: string; roles?: string[] } | null;

type AuthContextShape = {
  user: User;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<any>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextShape | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // helper: normalize/enrich user object so code can rely on `name` and `role`
  const normalizeUser = (u: any): User => {
    if (!u) return null;
    try {
      const out: any = { ...u };
      // normalize roles array if present (ensure lowercase strings)
      if (Array.isArray(out.roles)) {
        out.roles = out.roles.map((r: any) => String(r).toLowerCase());
        // derive primary role from roles array if explicit role missing
        if (!out.role) {
          if (out.roles.some((r: string) => r.includes("preparer"))) out.role = "preparer";
          else if (out.roles.some((r: string) => r.includes("approver"))) out.role = "approver";
        }
      }
      // prefer explicit role, otherwise infer from name/email for common test accounts
      if (!out.role) {
        if (typeof out.name === "string" && out.name.toLowerCase().includes("preparer")) out.role = "preparer";
        else if (typeof out.name === "string" && out.name.toLowerCase().includes("approver")) out.role = "approver";
        else if (typeof out.email === "string") {
          if (out.email.toLowerCase().includes("preparer")) out.role = "preparer";
          if (out.email.toLowerCase().includes("approver")) out.role = "approver";
        }
      }
      // normalize name casing
      if (out.role && !out.name) out.name = out.role.charAt(0).toUpperCase() + out.role.slice(1);
      return out;
    } catch {
      return null;
    }
  };

  // read user from localStorage or sessionStorage (prefer localStorage)
  const [user, setUser] = useState<User>(() => {
    try {
      const raw = localStorage.getItem("access_user") ?? sessionStorage.getItem("access_user");
      return raw ? normalizeUser(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  });

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return !!(localStorage.getItem("access_token") ?? sessionStorage.getItem("access_token"));
  });

  const [loading, setLoading] = useState<boolean>(false);

  // Optional: validate token on mount by calling /auth/me (if backend provides it)
  useEffect(() => {
    // currently we just trust stored token presence; if you have /auth/me, call it here
  }, []);

  const login = async (email: string, password: string, remember = true) => {
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      const token = res.data?.token ?? res.data?.access_token;
      if (!token) throw new Error("No token returned from server");

      // persist token and user according to remember flag
      if (remember) {
        localStorage.setItem("access_token", token);
      } else {
        sessionStorage.setItem("access_token", token);
      }

      const userFromRes = res.data?.user ?? null;
      if (userFromRes) {
        const normalized = normalizeUser(userFromRes);
        const serialized = JSON.stringify(normalized);
        if (remember) {
          localStorage.setItem("access_user", serialized);
          sessionStorage.removeItem("access_user");
        } else {
          sessionStorage.setItem("access_user", serialized);
          localStorage.removeItem("access_user");
        }
        setUser(normalized);
      } else {
        // clear opposite storage
        localStorage.removeItem("access_user");
        sessionStorage.removeItem("access_user");
        setUser(null);
      }

      setIsAuthenticated(true);
      // notify same-tab listeners (if any)
      window.dispatchEvent(new Event("auth:changed"));
      return res;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("access_user");
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("access_user");

  setUser(null);
  setIsAuthenticated(false);

  window.dispatchEvent(new Event("auth:changed"));

  // 🚀 Redirect instantly
  window.location.href = "/login";
};

  // listen to cross-tab storage changes
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "access_token") {
        const token = localStorage.getItem("access_token") ?? sessionStorage.getItem("access_token");
        setIsAuthenticated(!!token);
        const raw = localStorage.getItem("access_user") ?? sessionStorage.getItem("access_user");
        setUser(raw ? normalizeUser(JSON.parse(raw)) : null);
      }
    };

    // same-tab notifications (custom event) — in case code writes storage directly
    const onAuthChanged = () => {
      const token = localStorage.getItem("access_token") ?? sessionStorage.getItem("access_token");
      setIsAuthenticated(!!token);
      const raw = localStorage.getItem("access_user") ?? sessionStorage.getItem("access_user");
      setUser(raw ? normalizeUser(JSON.parse(raw)) : null);
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("auth:changed", onAuthChanged);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth:changed", onAuthChanged);
    };
  }, []);

  const value = useMemo(
    () => ({ user, isAuthenticated, loading, login, logout }),
    [user, isAuthenticated, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within AuthProvider");
  return ctx;
}

export default function useAuth() {
  return useAuthContext();
}
